import type { GetStaticProps } from 'next';
import type { FranchiseListSearch, FranchisePage } from 'interfaces/content/Ranking';
import type { HeaderNav } from 'interfaces/navigation/Header';
import SimpleRankingPage from 'pages/SimpleRanking/SimpleRankingPage';
import { Microsites } from 'constants/constants';
import handleStaticPropsError from 'utils/handleStaticPropsError';
import { getFranchiseBySlugAndYear } from 'api/Ranking/getFranchiseBySlugAndYear';
import getFooter from 'api/Footer/getFooter';
import getMenu from 'api/Header/getMenu';
import { setHeadData } from 'utils/metaDataUtils/headData';
import { setPianoConfig } from 'services/Piano';
import RankingCompanyFranchisePage from 'pages/RankingCompanyFranchise/RankingCompanyFranchisePage';
import type { PageAdConfig } from 'interfaces/ads/Ad';
import setFranchisePageAdConfig from 'services/Ad/config/setFranchisePageAdConfig';
import setPageGtmConfig from 'services/Gtm/setPageGtmConfig';
import { setarcSpanPageConfig } from 'services/ArcSpan';
import type { ArcSpanPageConfig } from 'services/ArcSpan/entities';
import type { PreviewData } from 'interfaces/PreviewData';
import { getFranchiseYearSearch } from 'api/Ranking/getFranchiseListItems';
import { safe, safeAsync } from 'utils/errorHandlingUtils';
import fallbackData from 'lib/fallbackData/commonData';
import { PianoPaywallPageType } from '@/app_lib/types/piano/Paywall';

interface Props {
  franchise: FranchisePage;
  franchiseSearch: FranchiseListSearch;
}

const AsiaRankingYear = ({ franchise, franchiseSearch }: Props) =>
  franchise.listType === 'grid' ? (
    <SimpleRankingPage
      franchiseList={franchise}
      subDomain='asia'
      hidePresentedBy
    />
  ) : (
    <RankingCompanyFranchisePage
      franchise={franchise}
      franchiseSearch={franchiseSearch}
    />
  );

export const getStaticProps: GetStaticProps = async (context) => {
  try {
    const platformCD = Microsites.Asia;
    const { franchiseSlug, year } = context.params as { franchiseSlug: string; year: string };
    const { previewData } = context as { previewData: PreviewData };
    const franchise = await getFranchiseBySlugAndYear(franchiseSlug, platformCD, year, previewData);
    const franchiseSearch = await safeAsync(
      () => getFranchiseYearSearch(franchiseSlug, year, previewData),
      {} as FranchiseListSearch,
    );
    const footer = await safeAsync(() => getFooter(), fallbackData.footer);
    const menu: HeaderNav = await safeAsync(
      () => getMenu('LEFT_MENU_COM', 'RIGHT_MENU_COM', 'TOPIC_NAVIGATION_COM', Microsites.Asia, null),
      fallbackData.headerNav,
    );
    const headData = safe(() => setHeadData(franchise, false, 'Fortune Asia'), fallbackData.headData);
    const arcSpanPageConfig: ArcSpanPageConfig = safe(
      () => setarcSpanPageConfig(franchise),
      fallbackData.arcSpanPageConfig,
    );
    const dataLayer = safe(() => setPageGtmConfig(franchise), fallbackData.dataLayer);
    const pianoPageConfig = safe(
      () => setPianoConfig({ pageType: PianoPaywallPageType.Ranking, pageData: franchise }),
      fallbackData.pianoPageConfig,
    );
    const pageAdConfig: PageAdConfig = safe(() => setFranchisePageAdConfig(franchise), fallbackData.pageAdConfig);

    return {
      props: {
        arcSpanPageConfig,
        dataLayer,
        footer,
        franchise,
        franchiseSearch,
        headData,
        headerNav: menu,
        pageAdConfig,
        pianoPageConfig,
      },
      revalidate: 60,
    };
  } catch (error) {
    return handleStaticPropsError(error as Error);
  }
};

export const getStaticPaths = () => ({
  fallback: 'blocking',
  paths: [],
});

export default AsiaRankingYear;
